import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { listen, type UnlistenFn } from '@tauri-apps/api/event';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { t2medMessages } from '../launch.messages';
import {
  T2MED_LAUNCH_READY_EVENT,
  T2MED_LAUNCH_REJECTED_EVENT,
  T2MED_LAUNCH_TOKEN_EXPIRED_EVENT,
  T2MED_NO_ONBOARDING_DETAIL_CODE,
  T2MED_PATIENT_RESOLVE_FAILED_EVENT,
  T2MED_WRITEBACK_SKIPPED_EVENT,
  T2MED_WRITEBACK_STATUS_EVENT,
  type T2medLaunchExpiredPayload,
  type T2medLaunchReadyPayload,
  type T2medLaunchRejectedPayload,
  type T2medPatientResolveFailedPayload,
  type T2medWritebackSkippedPayload,
  type T2medWritebackStatusPayload,
} from '../types';

/**
 * Subscribes to the T2med launch lifecycle events emitted by
 * `tauri-plugin-t2med` and turns them into navigation + user feedback.
 *
 * - `t2med:launch-ready` — the cloud verified the deep link and created or
 *   resumed a Heidi session: navigate straight to it. Patient resolution
 *   continues in the background Rust-side.
 * - `t2med:launch-rejected` — surface why (onboarding missing gets an
 *   actionable hint; everything else a generic retry message).
 * - `t2med:launch-token-expired` — the 4h APS token lapsed; writebacks
 *   pause until the next launch from T2med.
 * - `t2med:patient-resolve-failed` — non-blocking: the session works, the
 *   patient chip just isn't auto-filled.
 * - `t2med:writeback-skipped` — a polled note could not be written (no
 *   live token for that patient, or no API key). Deduped plugin-side.
 * - `t2med:writeback-status` — succeeded or failed once the cloud ACK
 *   lands. `pending` is accepted but not emitted until a live in-progress
 *   toast is wired.
 *
 * (`t2med:patient-resolved` needs no handler — the session page picks the
 * linked patient profile up through the normal session query refetch.)
 */
export function useT2medLaunchEvents(): void {
  const router = useRouter();
  const intl = useIntl();

  // Refs so listener registration survives router/intl identity changes.
  const routerRef = useRef(router);
  routerRef.current = router;
  const intlRef = useRef(intl);
  intlRef.current = intl;

  useEffect(() => {
    let isMounted = true;
    const unlisteners: UnlistenFn[] = [];

    const register = async <T>(
      event: string,
      handler: (payload: T) => void
    ): Promise<void> => {
      try {
        const unlisten = await listen<T>(event, (e) => {
          if (!isMounted) return;
          handler(e.payload);
        });
        if (!isMounted) {
          unlisten();
          return;
        }
        unlisteners.push(unlisten);
      } catch (error) {
        logger.error(
          `[t2med] failed to register ${event} listener`,
          error instanceof Error ? error.message : String(error)
        );
      }
    };

    void register<T2medLaunchReadyPayload>(T2MED_LAUNCH_READY_EVENT, (payload) => {
      logger.info('[t2med] launch ready, navigating', {
        sessionId: payload.session_id,
        resumed: payload.resumed,
      });
      toast({
        title: intlRef.current.formatMessage(
          payload.resumed
            ? t2medMessages.launchResumedTitle
            : t2medMessages.launchReadyTitle
        ),
      });
      void routerRef.current.push(`/scribe/session/${payload.session_id}`);
    });

    void register<T2medLaunchRejectedPayload>(T2MED_LAUNCH_REJECTED_EVENT, (payload) => {
      logger.warn('[t2med] launch rejected', {
        errorCode: payload.error_code,
        detailCode: payload.detail_code,
      });
      const description =
        payload.detail_code === T2MED_NO_ONBOARDING_DETAIL_CODE
          ? intlRef.current.formatMessage(t2medMessages.launchRejectedNoOnboarding)
          : intlRef.current.formatMessage(t2medMessages.launchRejectedGeneric);
      toast({
        tone: 'destructive',
        title: intlRef.current.formatMessage(t2medMessages.launchRejectedTitle),
        description,
      });
    });

    void register<T2medLaunchExpiredPayload>(
      T2MED_LAUNCH_TOKEN_EXPIRED_EVENT,
      // kontext_id is a patient-linked PVS identifier — deliberately NOT
      // logged (GDPR: no patient identifiers in forwarded logs).
      () => {
        logger.info('[t2med] launch token expired');
        toast({
          title: intlRef.current.formatMessage(t2medMessages.launchTokenExpiredTitle),
          description: intlRef.current.formatMessage(
            t2medMessages.launchTokenExpiredBody
          ),
        });
      }
    );

    void register<T2medPatientResolveFailedPayload>(
      T2MED_PATIENT_RESOLVE_FAILED_EVENT,
      (payload) => {
        logger.warn('[t2med] patient resolve failed', {
          sessionId: payload.session_id,
          errorCode: payload.error_code,
        });
        toast({
          title: intlRef.current.formatMessage(t2medMessages.patientResolveFailedTitle),
          description: intlRef.current.formatMessage(
            t2medMessages.patientResolveFailedBody
          ),
        });
      }
    );

    void register<T2medWritebackSkippedPayload>(
      T2MED_WRITEBACK_SKIPPED_EVENT,
      (payload) => {
        logger.info('[t2med] writeback skipped', {
          sessionId: payload.session_id,
          consultNoteLinkId: payload.consult_note_link_id,
        });
        toast({
          tone: 'destructive',
          title: intlRef.current.formatMessage(t2medMessages.writebackSkippedTitle),
          description: intlRef.current.formatMessage(t2medMessages.writebackSkippedBody),
        });
      }
    );

    void register<T2medWritebackStatusPayload>(
      T2MED_WRITEBACK_STATUS_EVENT,
      (payload) => {
        logger.info('[t2med] writeback status', {
          sessionId: payload.session_id,
          consultNoteLinkId: payload.consult_note_link_id,
          status: payload.status,
          errorCode: payload.error_code ?? undefined,
        });
        if (payload.status === 'pending') {
          toast({
            title: intlRef.current.formatMessage(t2medMessages.writebackPendingTitle),
          });
          return;
        }
        if (payload.status === 'succeeded') {
          toast({
            tone: 'success',
            title: intlRef.current.formatMessage(t2medMessages.writebackSucceededTitle),
          });
          return;
        }
        toast({
          tone: 'destructive',
          title: intlRef.current.formatMessage(t2medMessages.writebackFailedTitle),
          description: intlRef.current.formatMessage(
            writebackFailedDescription(payload.error_code)
          ),
        });
      }
    );

    return () => {
      isMounted = false;
      for (const unlisten of unlisteners) {
        unlisten();
      }
    };
  }, []);
}

function writebackFailedDescription(errorCode: string | null | undefined) {
  switch (errorCode) {
    case 'validation_failed':
    case 'transaction_rolled_back':
      return t2medMessages.writebackFailedValidation;
    case 'api_key_rejected':
      return t2medMessages.writebackFailedApiKey;
    case 'invalid_kontext':
      return t2medMessages.writebackFailedKontext;
    case 'profile_not_supported':
      return t2medMessages.writebackFailedProfile;
    case 'network':
    case 'feature_disabled':
      return t2medMessages.writebackFailedNetwork;
    default:
      return t2medMessages.writebackFailedGeneric;
  }
}
